import { useEffect, useMemo, useState } from 'react'
import { logger } from '../logger'
import BitcoinClient from '../clients/BitcoinClient'
import useWallet from './useWallet'
import { getEthProvider } from '../utils/getEthProvider'
import { executeAndSetInterval } from '../utils/executeAndSetInterval'
import { decimalsToE8s } from '../utils/validationHelper'
import { GasCalculator } from '../utils/GasCalculator'
import { Chain } from '../interfaces/common'

export const ETH_TRANSFER_GAS = 21000
export const ERC_20_DEPOSIT_GAS = 100000

function refetchValue<ReturnType>(args: any[], callback: (args: any[]) => Promise<ReturnType>) {
  const [value, setValue] = useState<ReturnType>()

  useEffect(() => {
    setValue(undefined)

    let interval: NodeJS.Timeout | number
    const timeout = setTimeout(() => {
      interval = executeAndSetInterval(async () => {
        try {
          const estimate = await callback(args)

          setValue(estimate)
        } catch (e) {
          if (!(e as any).reason?.includes('amount exceeds balance')) {
            logger.error(e as Error)
          }
        }
      }, 10000)
    }, 1000)

    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, args)

  return value
}

export const useETHGasFee = (
  isEnabled: boolean,
  chain: Chain | undefined,
  fromAddress: string | undefined,
  toAddress: string | undefined,
  wei: number | undefined,
  makeGasUsable: boolean | undefined
) => {
  const provider = useMemo(() => chain && getEthProvider(chain), [chain])

  const value = refetchValue([isEnabled, chain, wei], async ([enabled]) => {
    if (!enabled || !provider) return

    const [gasPrice, maxPriorityFeePerGas] = await Promise.all([
      provider.getGasPrice(),
      provider.estimateMaxPriorityFeePerGas?.().catch(() => 0n) ?? Promise.resolve(0n),
    ])

    let gasLimit = BigInt(0)
    const maxFeePerGas = gasPrice + maxPriorityFeePerGas

    if (fromAddress && toAddress && wei) {
      try {
        gasLimit = await provider.estimateGas({
          account: fromAddress as `0x${string}`,
          to: toAddress as `0x${string}`,
          value: BigInt(wei),
        })
      } catch (e) {
        return {
          failed: true,
          gasLimit: undefined,
          maxFeePerGas: makeGasUsable
            ? Math.floor(Number(maxFeePerGas) * 1.3)
            : Number(maxFeePerGas),
          maxPriorityFeePerGas: makeGasUsable
            ? Math.floor(Number(maxPriorityFeePerGas) * 1.3)
            : Number(maxPriorityFeePerGas),
        }
      }
    }

    return {
      failed: false,
      gasLimit: makeGasUsable ? Math.floor(Number(gasLimit) * 1.3) : Number(gasLimit),
      maxFeePerGas: makeGasUsable ? Math.floor(Number(maxFeePerGas) * 1.3) : Number(maxFeePerGas),
      maxPriorityFeePerGas: makeGasUsable
        ? Math.floor(Number(maxPriorityFeePerGas) * 1.3)
        : Number(maxPriorityFeePerGas),
    }
  })

  return (
    value || {
      failed: false,
      gasLimit: undefined,
      maxFeePerGas: undefined,
      maxPriorityFeePerGas: undefined,
    }
  )
}

export const useErc20TransferGas = (
  isEnabled: boolean,
  tokenAddress: string | undefined,
  transferAmount: number | undefined,
  chain: Chain | undefined
) => {
  const { wallet } = useWallet()
  const gasCalculator = useMemo(() => chain && new GasCalculator(chain), [chain])

  return refetchValue(
    [isEnabled, wallet.ethAddress, tokenAddress, transferAmount],
    async ([enabled, ethAddress, contractAddress, amount]) => {
      if (!enabled || !ethAddress || !tokenAddress || !gasCalculator) return

      const estimate = await gasCalculator.estimateErc20TransferGas(
        ethAddress,
        contractAddress,
        amount
      )

      return Number(estimate)
    }
  )
}

export const useErc20ApproveGas = (
  tokenAddress: string | undefined,
  transferAmount: number | undefined,
  chain: Chain,
  contractAddress?: string,
  makeGasUsable?: boolean
) => {
  const { wallet } = useWallet()
  const gasCalculator = useMemo(() => new GasCalculator(chain), [chain])

  return refetchValue(
    [wallet.ethAddress, tokenAddress, transferAmount, contractAddress],
    async ([ethAddress, tAddress, amount, cAddress]) => {
      if (!ethAddress || !tAddress || !cAddress) return

      const estimate = await gasCalculator.estimateErc20ApproveGas(
        ethAddress,
        tAddress,
        amount,
        cAddress
      )

      return makeGasUsable ? Math.floor(Number(estimate) * 1.3) : Number(estimate)
    }
  )
}

export const useEthDepositGas = (chain: Chain) => {
  const { wallet } = useWallet()
  const gasCalculator = useMemo(() => new GasCalculator(chain), [chain])

  return refetchValue([wallet.principalId], async ([principalId]) => {
    const estimate = await gasCalculator.estimateEthDepositGas(principalId)

    return Number(estimate)
  })
}

export const useBtcBaseFee = (isEnabled: boolean) => {
  return refetchValue([isEnabled], async (enabled) => {
    if (!enabled) return

    const fee = await BitcoinClient.getChainFee(4)

    return Number.parseInt(decimalsToE8s(`${fee.feerate}`, 8))
  })
}
